import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { Spinner } from '@/components/ui/Spinner'
import { useCreateProject } from '@/hooks/useProjects'
import { useClients } from '@/hooks/useClients'
import { useAdmins } from '@/hooks/useAdmins'
import { projectSchema, type ProjectFormData, PROJECT_STATUS_CONFIG } from '@/types/project'
import { ArrowLeft, FolderKanban } from 'lucide-react'

export default function NewProjectPage() {
  const navigate = useNavigate()
  const createProject = useCreateProject()
  const { data: clientsData, isLoading: loadingClients } = useClients({ limit: 1000 })
  const { data: adminsData, isLoading: loadingAdmins } = useAdmins()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      nom: '',
      description: '',
      status: 'pending',
      client_id: '',
      admin_id: null,
      budget: null,
      date_debut: null,
      date_fin: null,
    },
  })

  const onSubmit = async (data: ProjectFormData) => {
    try {
      const project = await createProject.mutateAsync(data)
      navigate(`/projects/${project.id}`)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : ''
      console.error('Erreur lors de la creation du projet:', errorMessage)
      alert('Erreur lors de la creation du projet. Veuillez reessayer.')
    }
  }

  const isLoading = loadingClients || loadingAdmins

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Projets', href: '/projects' },
          { label: 'Nouveau projet' },
        ]}
      />

      {/* Back button */}
      <Link to="/projects">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Retour
        </Button>
      </Link>

      <Card padding="lg">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-mint-100 flex items-center justify-center">
              <FolderKanban className="w-6 h-6 text-mint-600" />
            </div>
            <div>
              <CardTitle>Nouveau projet</CardTitle>
              <p className="text-sm text-brown-600 mt-1">
                Creez un nouveau projet pour un client
              </p>
            </div>
          </div>
        </CardHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Project Name */}
            <Input
              label="Nom du projet *"
              {...register('nom')}
              error={errors.nom?.message}
              placeholder="Ex: Site vitrine pour restaurant"
            />

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-brown-700">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={4}
                className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent resize-none"
                placeholder="Decrivez les objectifs et details du projet..."
              />
              {errors.description && (
                <p className="text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            {/* Client Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-brown-700">
                Client *
              </label>
              <Select
                {...register('client_id')}
                error={errors.client_id?.message}
              >
                <option value="">Selectionnez un client</option>
                {clientsData?.clients?.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.full_name} {client.company ? `(${client.company})` : ''}
                  </option>
                ))}
              </Select>
              {errors.client_id && (
                <p className="text-sm text-red-600">{errors.client_id.message}</p>
              )}
            </div>

            {/* Admin Assignment (optional) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-brown-700">
                Admin assigne (optionnel)
              </label>
              <Select {...register('admin_id')}>
                <option value="">Aucun admin assigne</option>
                {adminsData?.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {admin.full_name} ({admin.email})
                  </option>
                ))}
              </Select>
              {errors.admin_id && (
                <p className="text-sm text-red-600">{errors.admin_id.message}</p>
              )}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-brown-700">
                Statut *
              </label>
              <Select {...register('status')} error={errors.status?.message}>
                {Object.entries(PROJECT_STATUS_CONFIG).map(([value, config]) => (
                  <option key={value} value={value}>
                    {config.label}
                  </option>
                ))}
              </Select>
            </div>

            {/* Budget */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-brown-700">
                Budget (€)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                {...register('budget', {
                  setValueAs: (v) => v === '' || v === null ? null : parseFloat(v),
                })}
                error={errors.budget?.message}
                placeholder="Ex: 5000"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-brown-700">
                  Date de debut
                </label>
                <Input
                  type="date"
                  {...register('date_debut')}
                  error={errors.date_debut?.message}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-brown-700">
                  Date de fin prevue
                </label>
                <Input
                  type="date"
                  {...register('date_fin')}
                  error={errors.date_fin?.message}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-beige-200">
              <Link to="/projects">
                <Button type="button" variant="outline">
                  Annuler
                </Button>
              </Link>
              <Button
                type="submit"
                disabled={isSubmitting}
                leftIcon={isSubmitting ? <Spinner size="sm" /> : <FolderKanban className="w-4 h-4" />}
              >
                {isSubmitting ? 'Creation...' : 'Creer le projet'}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  )
}
